import { useClipboard } from "@vueuse/core";
import { ElMessage } from 'element-plus';
import { getMiniProgram, waitForMiniProgram } from '@/utils/wechat'
import { getDeviceId } from '@/utils/device'

const { copy, isSupported } = useClipboard();

// 与用户验证相关的方法
const key = 'identity'

/**
 * 获取用户 token
 */
export function getToken() {
  // 从 localStorage 获取 token
  return localStorage.getItem('ydppt_token')
}

export function setToken(token) {
  // 本地缓存存储 token
  localStorage.setItem(`ydppt_token`, token)
}

export function removeToken() {
  // 本地缓存移除 token
  sessionStorage.removeItem(`${key}_token`)
  localStorage.removeItem(`ydppt_token`)
}

// 获取 url 参数
export const getQueryParam = (paramName) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(paramName) || null;
};

/** 是否运行在小程序 web-view 内 */
export function isMiniAppWebview(): boolean {
  if (getQueryParam('from') === 'miniapp') return true
  try {
    if ((window as unknown as { __wxjs_environment?: string }).__wxjs_environment === 'miniprogram') {
      return true
    }
  } catch {
    /* ignore */
  }
  return /miniProgram/i.test(navigator.userAgent || '')
}

export type MobileDeviceParams = {
  version: string
  versionCode: string
  pkg: string
  platform: string
  di: string
  oi: string
  dn: string
}

/**
 * 微信小程序 web-view 的 SSE / 业务公共设备参数。
 * iOS 小程序也有 window.prompt，但不能走 App 的 ydppt:// 桥，必须固定 WECHAT_MINI。
 */
export function getMiniAppDeviceParams(): MobileDeviceParams {
  let di = ''
  try {
    di = localStorage.getItem('device_id') || ''
  } catch {
    /* ignore */
  }
  if (!di) {
    try {
      di = getDeviceId() || ''
    } catch {
      di = `mini_${Date.now().toString(36)}`
    }
  }
  return {
    version: '1.2.0',
    versionCode: '120',
    pkg: 'ydppt_wechat',
    platform: 'WECHAT_MINI',
    di,
    oi: '',
    dn: '',
  }
}

/** 解析移动端设备参数：小程序优先，否则合并 store / 默认空值 */
export function resolveMobileDeviceParams(
  parsedData?: Partial<MobileDeviceParams> | null,
): MobileDeviceParams {
  if (isMiniAppWebview()) {
    const base = getMiniAppDeviceParams()
    const di = String(parsedData?.di || '').trim()
    return di ? { ...base, di } : base
  }
  return {
    version: parsedData?.version || '',
    versionCode: parsedData?.versionCode || '',
    pkg: parsedData?.pkg || '',
    platform: parsedData?.platform || '',
    di: parsedData?.di || '',
    oi: parsedData?.oi || '',
    dn: parsedData?.dn || '',
  }
}

export function syncMiniAppTokenFromQuery(): string {
  if (!isMiniAppWebview()) {
    return getToken() || ''
  }

  const params = new URLSearchParams(window.location.search)
  // URL 未带 token 参数时：可能是 H5 内部跳转，保留现有 localStorage
  if (!params.has('token')) {
    return getToken() || ''
  }

  const token = params.get('token') || ''
  // 拦截错误拼接产生的脏值，例如 "?from=miniapp"
  const isValidToken = !!token && !token.startsWith('?') && !token.includes('from=') && !token.includes('&')

  if (isValidToken) {
    setToken(token)
    return token
  }

  // token 为空或脏值：清除 H5 残留
  removeToken()
  return ''
}

export function getMiniAppRedirectPath(): string {
  // 只回跳路径本身，去掉 from/token 等小程序参数，避免二次拼进 getWebUrl
  const pathname = location.pathname || '/m/home'
  const params = new URLSearchParams(location.search || '')
  params.delete('from')
  params.delete('token')
  params.delete('t')
  const qs = params.toString()
  return qs ? `${pathname}?${qs}` : pathname
}

export function requestMiniAppLogin(redirectPath?: string): boolean {
  if (!isMiniAppWebview()) return false

  const target = redirectPath || getMiniAppRedirectPath()
  const loginUrl = `/pages/login/index?redirect=${encodeURIComponent(target)}`

  try {
    const miniProgram = getMiniProgram()
    if (miniProgram?.navigateTo) {
      miniProgram.navigateTo({ url: loginUrl })
      return true
    }

    void waitForMiniProgram().then((runtime) => {
      if (runtime?.navigateTo) {
        runtime.navigateTo({ url: loginUrl })
        return
      }

      console.warn('miniapp login bridge unavailable: wx.miniProgram.navigateTo not ready')
    })

    return true
  } catch (error) {
    console.error('miniapp login bridge navigate failed:', error)
  }

  return false
}

/** 小程序 tabBar 页面路径（与 yidianppt-miniapp pages.json 一致） */
const MINIAPP_TAB_MAP: Record<string, string> = {
  home: '/pages/home/index',
  create: '/pages/home/index',
  template: '/pages/template/index',
  member: '/pages/member/index',
  production: '/pages/production/index',
  mine: '/pages/mine/index',
}

/**
 * H5 web-view → 小程序原生 tab 切换（等同点击底部 tab）
 * 会员 Tab 特殊：全屏 webview 打开 /m/Member（与原生 Tab 点击一致）
 */
export function requestMiniAppSwitchTab(tab: string): boolean {
  if (!isMiniAppWebview()) return false

  if (tab === 'member') {
    return requestMiniAppOpenWebview({
      path: '/m/Member',
      title: '会员中心',
    })
  }

  const url = MINIAPP_TAB_MAP[tab]
  if (!url) {
    console.warn('miniapp switchTab unknown tab:', tab)
    return false
  }

  try {
    const miniProgram = getMiniProgram()
    if (miniProgram?.switchTab) {
      miniProgram.switchTab({ url })
      return true
    }

    void waitForMiniProgram().then((runtime) => {
      if (runtime?.switchTab) {
        runtime.switchTab({ url })
        return
      }
      console.warn('miniapp switchTab bridge unavailable: wx.miniProgram.switchTab not ready')
    })

    return true
  } catch (error) {
    console.error('miniapp switchTab bridge failed:', error)
  }

  return false
}

/** H5 web-view → 小程序返回上一页 */
export function requestMiniAppNavigateBack(delta = 1): boolean {
  if (!isMiniAppWebview()) return false

  try {
    const miniProgram = getMiniProgram()
    if (miniProgram?.navigateBack) {
      miniProgram.navigateBack({ delta })
      return true
    }

    void waitForMiniProgram().then((runtime) => {
      if (runtime?.navigateBack) {
        runtime.navigateBack({ delta })
        return
      }
      console.warn('miniapp navigateBack bridge unavailable: wx.miniProgram.navigateBack not ready')
    })

    return true
  } catch (error) {
    console.error('miniapp navigateBack bridge failed:', error)
  }

  return false
}

export type MiniAppVirtualPayLaunchParams = {
  catalogCode: string
  payPrice?: string | number
  payTitle?: string
}

/**
 * H5 web-view → 小程序原生虚拟支付页
 * 仅传套餐信息；下单与拉起支付由原生页完成
 */
export function requestMiniAppVirtualPay(
  params: MiniAppVirtualPayLaunchParams,
  options: { auto?: boolean } = { auto: true }
): boolean {
  if (!isMiniAppWebview()) return false
  if (!params?.catalogCode) {
    console.warn('miniapp virtual pay catalogCode missing', params)
    return false
  }

  const auto = options.auto !== false ? '1' : '0'
  const query = [
    `auto=${auto}`,
    `catalogCode=${encodeURIComponent(params.catalogCode)}`,
    `payPrice=${encodeURIComponent(String(params.payPrice ?? ''))}`,
    `payTitle=${encodeURIComponent(String(params.payTitle ?? ''))}`,
  ].join('&')
  const url = `/pages/virtual-pay/index?${query}`

  try {
    const miniProgram = getMiniProgram()
    if (miniProgram?.navigateTo) {
      miniProgram.navigateTo({ url })
      return true
    }

    void waitForMiniProgram().then((runtime) => {
      if (runtime?.navigateTo) {
        runtime.navigateTo({ url })
        return
      }
      console.warn('miniapp virtual pay bridge unavailable: wx.miniProgram.navigateTo not ready')
    })

    return true
  } catch (error) {
    console.error('miniapp virtual pay navigate failed:', error)
  }

  return false
}

export type MiniAppDownloadFile = {
  fileUrl: string
  fileName: string
  fileType: string
}

/**
 * H5 web-view → 小程序原生下载页
 * 单文件走 query；多文件走 files=JSON（过长时由调用方先上传 manifest）
 */
export function requestMiniAppDownload(files: MiniAppDownloadFile | MiniAppDownloadFile[]): boolean {
  if (!isMiniAppWebview()) return false

  const list = Array.isArray(files) ? files : [files]
  if (!list.length) return false

  let loginUrl = ''
  if (list.length === 1) {
    const f = list[0]
    loginUrl =
      `/pages/download/index?fileUrl=${encodeURIComponent(f.fileUrl)}` +
      `&fileName=${encodeURIComponent(f.fileName)}` +
      `&fileType=${encodeURIComponent(f.fileType || '')}`
  } else {
    loginUrl = `/pages/download/index?files=${encodeURIComponent(JSON.stringify(list))}`
  }

  try {
    const miniProgram = getMiniProgram()
    if (miniProgram?.navigateTo) {
      miniProgram.navigateTo({ url: loginUrl })
      return true
    }

    void waitForMiniProgram().then((runtime) => {
      if (runtime?.navigateTo) {
        runtime.navigateTo({ url: loginUrl })
        return
      }
      console.warn('miniapp download bridge unavailable: wx.miniProgram.navigateTo not ready')
    })

    return true
  } catch (error) {
    console.error('miniapp download bridge navigate failed:', error)
  }

  return false
}

/** 小程序无 tab 全屏路径白名单（由入口打开；showAndiroTab(false) 不再二次开页） */
const MINIAPP_FULLSCREEN_PATHS: { prefix: string; title: string }[] = [
  { prefix: '/m/Template/detail', title: '模板详情' },
  { prefix: '/m/create', title: '生成大纲' },
  { prefix: '/mobile', title: '编辑' },
]

function matchMiniAppFullscreenPath(pathname = '') {
  return MINIAPP_FULLSCREEN_PATHS.find(
    (item) => pathname === item.prefix || pathname.startsWith(`${item.prefix}/`)
  )
}

/** 全屏页返回时应 switchTab 的 tab（navigateBack 失败时的兜底） */
function resolveMiniAppFromTab(path = '', explicit?: string): string {
  if (explicit && MINIAPP_TAB_MAP[explicit]) return explicit

  const qIndex = path.indexOf('?')
  const pathname = qIndex >= 0 ? path.slice(0, qIndex) : path
  const query = qIndex >= 0 ? path.slice(qIndex + 1) : ''
  const fromMatch = /(?:^|&)from=([^&]*)/.exec(query)
  const from = fromMatch ? decodeURIComponent(fromMatch[1]) : ''

  if (from === 'home' || from === 'production' || from === 'template') return from
  if (pathname.startsWith('/m/create')) return 'home'
  if (pathname.startsWith('/m/Template')) return 'template'
  if (pathname.startsWith('/mobile')) return 'production'
  return 'home'
}

/**
 * H5 web-view → 小程序通用 webview 页（协议、外部说明页、无 tab 全屏页等）
 * - path: H5 路由，如 /user-agreement、/m/Template/detail?id=xx
 * - url: 完整 URL（优先于 path）
 * - 全屏白名单页用 navigateTo 叠栈，微信左上角显示返回而非小房子
 */
let miniOpenWebviewBusy = false
let miniOpenWebviewLastKey = ''
let miniOpenWebviewLastAt = 0

/** 小程序页面 url 过长会被截断，预留 path/title/fromTab 空间 */
const MINIAPP_PAGE_URL_BUDGET = 1800

export function requestMiniAppOpenWebview(options: {
  path?: string
  url?: string
  title?: string
  /** 无微信原生导航栏（白名单全屏页） */
  fullscreen?: boolean
  /** 全屏页返回时 switchTab 的目标 tab */
  fromTab?: string
  /** 传给新开 H5 的草稿 JSON（写入 H5 URL，解决独立 web-view 丢 Pinia） */
  h5Draft?: string
}): boolean {
  if (!isMiniAppWebview()) return false

  const title = options.title || '详情'
  const key = options.url || options.path || ''
  const now = Date.now()
  // 防止连点 / 入口+生命周期重复打开多层
  if (
    miniOpenWebviewBusy
    || (key && key === miniOpenWebviewLastKey && now - miniOpenWebviewLastAt < 1600)
  ) {
    return true
  }

  const pathName = String(options.path || '').split('?')[0] || ''
  const fullscreen = options.fullscreen ?? !!matchMiniAppFullscreenPath(pathName)
  const pageBase = fullscreen
    ? '/pages/fullscreen-webview/index'
    : '/pages/webview/index'
  const fromTab = fullscreen
    ? resolveMiniAppFromTab(options.path || '', options.fromTab)
    : ''

  let pageUrl = ''
  if (options.url) {
    pageUrl =
      `${pageBase}?url=${encodeURIComponent(options.url)}` +
      `&title=${encodeURIComponent(title)}`
  } else if (options.path) {
    pageUrl =
      `${pageBase}?path=${encodeURIComponent(options.path)}` +
      `&title=${encodeURIComponent(title)}`
  } else {
    console.warn('miniapp open webview missing path/url', options)
    return false
  }

  if (fromTab) {
    pageUrl += `&fromTab=${encodeURIComponent(fromTab)}`
  }

  // 草稿优先走 localStorage；URL 仅在长度允许时附带，避免 navigate/redirect 截断丢参
  if (options.h5Draft) {
    const encodedDraft = encodeURIComponent(options.h5Draft)
    if (pageUrl.length + encodedDraft.length + 12 < MINIAPP_PAGE_URL_BUDGET) {
      pageUrl += `&h5Draft=${encodedDraft}`
    }
  }

  miniOpenWebviewBusy = true
  miniOpenWebviewLastKey = key
  miniOpenWebviewLastAt = now
  window.setTimeout(() => {
    miniOpenWebviewBusy = false
  }, 800)

  const openPage = (runtime: { navigateTo?: Function; redirectTo?: Function } | undefined) => {
    if (!runtime) return false
    // 统一 navigateTo 叠栈：胶囊旁显示返回箭头，而不是小房子
    if (runtime.navigateTo) {
      runtime.navigateTo({ url: pageUrl })
      return true
    }
    if (runtime.redirectTo) {
      runtime.redirectTo({ url: pageUrl })
      return true
    }
    return false
  }

  try {
    const miniProgram = getMiniProgram()
    if (openPage(miniProgram)) return true

    void waitForMiniProgram().then((runtime) => {
      if (openPage(runtime)) return
      console.warn('miniapp webview bridge unavailable: navigateTo/redirectTo not ready')
    })

    return true
  } catch (error) {
    miniOpenWebviewBusy = false
    console.error('miniapp webview navigate failed:', error)
  }

  return false
}

/** 当前是否在小程序「无 tab」独立 web-view 中 */
export function isMiniAppStandalone(): boolean {
  return isMiniAppWebview() && getQueryParam('miniStandalone') === '1'
}

/**
 * 独立 web-view 内 H5 路由跳转时，合并保留 miniStandalone / fromTab / from=miniapp
 * 否则 /mobile 等页丢失参数后，返回会变成 web-view 内 router.push 首页
 */
export function withMiniAppStandaloneQuery(
  query: Record<string, string | null | undefined> = {}
): Record<string, string> {
  const next: Record<string, string> = {}
  Object.entries(query).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      next[key] = String(value)
    }
  })
  if (!isMiniAppStandalone()) return next

  next.from = 'miniapp'
  next.miniStandalone = '1'
  const fromTab = getQueryParam('fromTab')
  if (fromTab) next.fromTab = fromTab
  return next
}

/**
 * 按白名单打开无 tab 独立页（供入口调用）
 */
export function requestMiniAppFullscreen(path: string, title?: string): boolean {
  if (!isMiniAppWebview()) return false
  if (isMiniAppStandalone()) return true

  const pathname = String(path || '').split('?')[0] || ''
  const matched = matchMiniAppFullscreenPath(pathname)
  if (!matched) return false

  return requestMiniAppOpenWebview({
    path,
    title: title || matched.title,
    fullscreen: true,
  })
}

/**
 * 小程序 tab 显隐替代：
 * - hide: 不再开新页（避免与入口叠多层），仅由 requestMiniAppFullscreen / OpenWebview 打开
 * - show: 优先 navigateBack（叠栈返回）；失败再 switchTab 回 fromTab
 */
export function requestMiniAppTabVisible(show: boolean): boolean {
  if (!isMiniAppWebview()) return false

  if (!show) {
    // 已在独立页或仍在 tab 页：都不在这里 navigateTo
    return true
  }

  if (!isMiniAppStandalone()) return false

  const fromTab = getQueryParam('fromTab') || 'home'
  const url = MINIAPP_TAB_MAP[fromTab] || MINIAPP_TAB_MAP.home

  const goBack = (runtime: {
    navigateBack?: (options?: {
      delta?: number
      fail?: (err?: unknown) => void
    }) => void
    switchTab?: (options: { url: string }) => void
  } | undefined) => {
    if (!runtime) return false
    if (runtime.navigateBack) {
      runtime.navigateBack({
        delta: 1,
        fail: () => {
          runtime.switchTab?.({ url })
        },
      })
      return true
    }
    if (runtime.switchTab) {
      runtime.switchTab({ url })
      return true
    }
    return false
  }

  try {
    const miniProgram = getMiniProgram()
    if (goBack(miniProgram)) return true
    void waitForMiniProgram().then((runtime) => {
      if (!goBack(runtime)) {
        console.warn('miniapp navigateBack/switchTab not ready')
      }
    })
    return true
  } catch (error) {
    console.error('miniapp navigateBack/switchTab failed:', error)
  }

  return false
}

/**
 * 多文件下载：若 query 过长则先上传 JSON 清单，再只传 manifestUrl
 */
export async function requestMiniAppDownloadSafe(files: MiniAppDownloadFile[]): Promise<boolean> {
  if (!isMiniAppWebview() || !files.length) return false

  if (files.length === 1) {
    return requestMiniAppDownload(files[0])
  }

  const json = JSON.stringify(files)
  if (json.length <= 700) {
    return requestMiniAppDownload(files)
  }

  try {
    const userApi = await import('@/api/user')
    const signRes = await userApi.getFileSign({
      params: { fileType: 'JSON', source: 'TEMPORARY_FILE' },
    }) as { code: string; data: { sign: string; fileUrl: string } }

    if (signRes.code !== 'SUCCESS' || !signRes.data?.sign) {
      return requestMiniAppDownload(files[0])
    }

    const putRes = await fetch(signRes.data.sign, {
      method: 'PUT',
      body: json,
      headers: { 'Content-Type': 'application/json' },
    })
    if (putRes.status !== 200) {
      return requestMiniAppDownload(files[0])
    }

    const url =
      `/pages/download/index?manifestUrl=${encodeURIComponent(signRes.data.fileUrl)}`

    const miniProgram = getMiniProgram()
    if (miniProgram?.navigateTo) {
      miniProgram.navigateTo({ url })
      return true
    }
    void waitForMiniProgram().then((runtime) => {
      runtime?.navigateTo?.({ url })
    })
    return true
  } catch (error) {
    console.error('miniapp download manifest failed:', error)
    return requestMiniAppDownload(files[0])
  }
}

export function ensureNativeLogin(redirectPath?: string): boolean {
  if (isMiniAppWebview()) {
    syncMiniAppTokenFromQuery()
    if (getToken()) return true
    requestMiniAppLogin(redirectPath)
    return false
  }

  if (window.JsAndroid && JsAndroid.isLogin && !JsAndroid.isLogin()) {
    JsAndroid.login()
    return false
  }

  if (window.prompt) {
    const isLogin = window.prompt('ydppt://isLogin')
    const isLoggedIn = isLogin !== null && isLogin !== undefined && isLogin !== 'false'
    if (!isLoggedIn) {
      window.prompt('ydppt://login')
      return false
    }
  }

  return true
}

/**
 * 防抖函数
 * @param {Function} func 要执行的回调函数
 * @param {Number} delay 延迟时间（单位：毫秒）
 * @param {Boolean} immediate 是否立即执行（第一次不等待 delay）
 */
export function debounce(func, delay = 300, immediate = false) {
  let timer;

  return function (...args) {
    const context = this;

    if (timer) clearTimeout(timer);

    if (immediate && !timer) {
      func.apply(context, args);
      timer = setTimeout(() => {
        timer = null;
      }, delay);
    } else {
      timer = setTimeout(() => {
        func.apply(context, args);
        timer = null;
      }, delay);
    }
  };
}

// 安卓页面 tab 显示方法
// show: true 显示，false 隐藏，默认为 false
export const showAndiroTab = (show = false) => {
  // 小程序：白名单全屏页用独立 web-view（无 tab）；返回时 navigateBack
  if (isMiniAppWebview()) {
    requestMiniAppTabVisible(!!show)
    return
  }
  // 判断是不是 tab 栏页面，是就走显示 tab 栏
  const isTab = getQueryParam('showTab');
  if (window.JsAndroid) {
    if (isTab) {
      JsAndroid.showTabBar(true);
    } else {
      JsAndroid.showTabBar(show);
    }
  } else if (window.prompt) {
    if (isTab) {
      window.prompt("ydppt://showTabBar?isShow=true")
    } else {
      window.prompt(`ydppt://showTabBar?isShow=${show}`)
    }
  }
}

// 充值弹窗（App 半屏 / 小程序全屏 web-view 打开会员页）
export const payment = (type: 'member' | 'integral', price?: number) => {
  // 微信小程序：ydppt://halfcharge 无效，走原生 webview 打开会员/积分页
  if (isMiniAppWebview()) {
    const integralPrice = price ?? 33
    const path =
      type === 'member'
        ? '/m/Member?memberType=Member&retention=true'
        : `/m/Member?memberType=Integral&retention=true&integralPrice=${integralPrice}`
    requestMiniAppOpenWebview({
      path,
      title: type === 'member' ? '开通会员' : '积分充值',
    })
    return
  }

  if (type === 'member') {
    if (window.JsAndroid) {
      window.location.href = ' ydppt://halfcharge?rate=1&integralPrice=33&url=' + location.origin + '%2Fm/Member%3FmemberType%3DMember%26retention%3Dtrue';
    } else {
      window.location.href = 'ydppt://halfcharge?url=/m/Member%3FmemberType%3DMember%26nonMember%3Dtrue&isHalf=true&rate=1&integralPrice=33';
    }
  } else {
    if (window.JsAndroid) {
      window.location.href = ' ydppt://halfcharge?rate=1&integralPrice=33&url=' + location.origin + '%2Fm/Member%3FmemberType%3DIntegral%26retention%3Dtrue';
    } else {
      window.location.href = `ydppt://halfcharge?url=/m/Member%3FmemberType%3DIntegral%26integralPrice%3D33&isHalf=true&rate=1`;
    }
  }
}

// 检查积分是否足够生成
// 生成一个 PPT 需要 10 积分
export const checkIntegral = async () => {
  const { useUserStore } = await import('@/store'); // 动态导入，避免循环依赖
  // const userStore = useUserStore();

  // const userInfo = userStore.getUser;
  const UserApi = await import('@/api/user.ts'); // 动态导入 API 模块以避免循环依赖

  // 调用用户信息接口
  const response = await UserApi.getUserInfo({});
  const userInfo = response.data;
  let balance = userInfo?.account?.freeBalance + userInfo?.account?.permanentBalance;
  if (!balance) {
    balance = 0;
  }
  return balance < 10;
}

// 获取安卓端信息
export function getAndrioInfo() {
  try {
    if (window.JsAndroid) {
      const dpr = window.devicePixelRatio || 1; // 设备像素比
      const andrioInfo = {};
      andrioInfo.barHeight = (JsAndroid.getStatusHeight() / dpr) + 5;
      andrioInfo.uid = JsAndroid.getUid();
      andrioInfo.token = JsAndroid.getToken();
      // 获取 token
      setToken(JsAndroid.getToken());
      andrioInfo.version = JsAndroid.getVersion();
      andrioInfo.versionCode = JsAndroid.getVersionCode();
      andrioInfo.pkg = JsAndroid.getPkg();
      andrioInfo.platform = JsAndroid.getPlatform();
      andrioInfo.di = JsAndroid.getDeviceId();
      andrioInfo.dn = JsAndroid.getDeviceName();
      andrioInfo.oi = JsAndroid.getOAID();
      return andrioInfo;
    }
  } catch (e) {
    // console.warn('调用 JsAndroid 失败:', e.message);
  }
}

/**
 * 常在 Safari / 系统浏览器单独打开的页面，不需要拉取原生设备信息。
 * 此类路由下直接置空 prompt，避免 Safari 弹出 getPlatform 等协议对话框。
 */
export const IOS_WEB_ONLY_ROUTE_PATHS = [
  '/',
  '/Website',
  '/m/Website',
  '/m/website',
  '/user-agreement',
  '/privacy-policy',
  '/child-privacy-policy',
  '/third-party-sdk-list',
  '/pay-policy',
  '/integral-rule',
] as const

const normalizeRoutePath = (path: string) => {
  const p = path.split('?')[0].replace(/\/$/, '') || '/'
  return p.toLowerCase()
}

export function isIOSWebOnlyRoute(path?: string): boolean {
  const normalized = normalizeRoutePath(path ?? (typeof location !== 'undefined' ? location.pathname : '/'))
  return IOS_WEB_ONLY_ROUTE_PATHS.some(
    (route) => normalizeRoutePath(route) === normalized
  )
}

const IOS_NATIVE_SESSION_KEY = 'ydppt_ios_native'

/** 当前页是否应跳过 iOS 原生桥接 */
export function shouldSkipIOSNativeBridge(path?: string): boolean {
  if (!isIOSWebOnlyRoute(path)) return false
  // App 内带 showTab，或本会话已成功拉过原生信息时，仍保留桥接
  if (getQueryParam('showTab')) return false
  try {
    if (sessionStorage.getItem(IOS_NATIVE_SESSION_KEY) === '1') return false
  } catch {
    /* ignore */
  }
  return true
}

export function markIOSNativeSession(): void {
  try {
    sessionStorage.setItem(IOS_NATIVE_SESSION_KEY, '1')
  } catch {
    /* ignore */
  }
}

/** 从 iOS 原生桥接读取底部 Tab 栏高度（px），非 App 环境返回 null */
export function fetchIOSTabBarHeight(): number | null {
  if (!window.prompt) return null
  try {
    const raw = window.prompt('ydppt://getTabBarHeight')
    if (!raw) return null
    const height = Number(raw)
    return Number.isFinite(height) && height > 0 ? height : null
  } catch {
    return null
  }
}

/** store 中尚无高度时，从原生桥接同步（供页面 onMounted 兜底，避免 App.vue 延迟写入前读到 0） */
export function syncTabBarHeightIfNeeded(
  currentHeight: number,
  setTabBarHeight: (height: number) => void,
): void {
  if (currentHeight > 0) return
  const height = fetchIOSTabBarHeight()
  if (height != null) setTabBarHeight(height)
}

/** 是否成功拿到 iOS 原生端信息（App WebView 会返回 platform=IOS） */
export function isValidIOSNativeInfo(data: Record<string, unknown> | null | undefined): boolean {
  return !!data && String(data.platform).toUpperCase() === 'IOS'
}

// 获取 iOS 端信息
export function getIOSInfo(path?: string) {
  try {
    if (isMiniAppWebview()) {
      return null
    }
    if (shouldSkipIOSNativeBridge(path)) {
      return null
    }
    // 只在 iOS 环境下执行
    if (window.prompt && /iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      const platform = window.prompt("ydppt://getPlatform") || '';
      if (String(platform).toUpperCase() !== 'IOS') {
        return null
      }

      const dpr = window.devicePixelRatio || 1; // 设备像素比
      const IOSInfo: Record<string, unknown> = { platform };

      IOSInfo.uid = window.prompt("ydppt://getUid") || '';
      IOSInfo.token = window.prompt("ydppt://getToken") || '';
      setToken(IOSInfo.token as string);

      const statusHeight = parseFloat(window.prompt("ydppt://getStatusHeight") || '0') || 0;

      // 使用 CSS 检测灵动岛
      const isDynamicIslandDevice = checkDynamicIslandByCSS();
      // 根据设备类型设置不同的状态栏高度
      if (isDynamicIslandDevice) {
        // 对于有灵动岛的设备，使用更大的安全区域
        IOSInfo.barHeight = Math.max(((statusHeight / dpr) + 35), 38);
      } else {
        // 对于刘海屏或普通设备保持原有逻辑
        IOSInfo.barHeight = (statusHeight / dpr) + 24;
      }

      IOSInfo.version = window.prompt("ydppt://getVersion") || '';
      IOSInfo.versionCode = window.prompt("ydppt://getVersionCode") || '';
      IOSInfo.pkg = window.prompt("ydppt://getPkg") || '';
      IOSInfo.di = window.prompt("ydppt://getDeviceId") || '';
      IOSInfo.dn = window.prompt("ydppt://getDeviceName") || '';
      IOSInfo.oi = window.prompt("ydppt://getOAID") || '';

      return IOSInfo;
    }
  } catch (e) {
    // console.warn('调用 iOS prompt 失败:', e.message);
    return null;
  }
}

/**
 * 使用 safe-area-inset-top 环境变量检测是否为灵动岛设备。
 * @returns {boolean} 是否为灵动岛设备。
 */
function checkDynamicIslandByCSS() {
  // 1. 创建一个临时的测试元素
  const testElement = document.createElement('div');

  // 2. 设置样式：应用与顶部安全区域相关的 CSS 变量
  // 注意：需要将该元素附加到 DOM 中，才能正确计算其样式
  testElement.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        visibility: hidden;
        /* 使用 env() 确保浏览器能计算安全区域的高度 */
        height: env(safe-area-inset-top, 0px); 
    `;

  document.body.appendChild(testElement);

  // 3. 获取计算后的高度值
  // 使用 getComputedStyle 确保获取的是浏览器实际计算出的像素值
  const computedHeight = parseFloat(window.getComputedStyle(testElement).height);

  // 4. 清理 DOM
  document.body.removeChild(testElement);

  // 5. 判断逻辑
  // 灵动岛 (iPhone 14 Pro / 15 Pro) 的 safe-area-inset-top 约为 59px (逻辑像素)
  // 刘海屏 (iPhone 13 / 14) 的 safe-area-inset-top 约为 47px (逻辑像素)
  // 阈值设置为 50px 可以有效区分两者
  const DYNAMIC_ISLAND_THRESHOLD = 50;

  // 仅在 iOS 设备上执行这个判断
  const isiOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (isiOS && computedHeight > DYNAMIC_ISLAND_THRESHOLD) {
    return true;
  }

  return false;
}

/**
 * 判断是否是移动端访问
 * @returns {boolean}
 */
export function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/** 原生权限名称：storge 存储 / phone 电话 / record 录音 */
export type NativePermissionName = 'storge' | 'phone' | 'record'

const parseNativeBoolean = (value: string | null | undefined): boolean => {
  return value !== null && value !== undefined && value !== 'false' && value !== '0'
}

/**
 * 检查是否有某个权限
 * @param permName 权限名称：storge 存储 / phone 电话 / record 录音
 * @returns true 有权限；false 没有权限
 */
export function isGrantedPermission(permName: NativePermissionName): boolean {
  try {
    if (window.JsAndroid?.isGrantedPermission) {
      return !!window.JsAndroid.isGrantedPermission(permName)
    }
    if (window.prompt) {
      return true
      // const result = window.prompt(`ydppt://isGrantedPermission?permName=${encodeURIComponent(permName)}`)
      // return parseNativeBoolean(result)
    }
  } catch {
    /* ignore */
  }
  return false
}

/**
 * 请求某个权限
 * @param permName 权限名称：storge 存储 / phone 电话 / record 录音
 * @param tip 申请权限提示文案
 */
export function requestPermission(permName: NativePermissionName, tip: string): void {
  try {
    if (window.JsAndroid?.requestPermission) {
      window.JsAndroid.requestPermission(permName, tip)
      return
    }
    if (window.prompt) {
      window.prompt(
        `ydppt://requestPermission?permName=${encodeURIComponent(permName)}&tip=${encodeURIComponent(tip)}`
      )
    }
  } catch {
    /* ignore */
  }
}

//数据上报公共方法
export function reportEvent(event, params) {
  try {
    // 小程序 web-view：走 H5 collectEvent（deviceType=微信小程序），不转发原生桥
    if (isMiniAppWebview()) {
      if (window.collectEvent) {
        window.collectEvent(event, params)
      }
      return
    }

    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    // 尝试调用安卓的 onEvent 方法
    if (window.JsAndroid && JsAndroid.onEvent) {
      const result = JsAndroid.onEvent(event, JSON.stringify(params));
      // 如果返回 false 或 undefined，表示调用失败或不支持，进入 fallback
      if (result === false) {
        throw new Error('Android onEvent returned false');
      }
      return;
    } else if (window.prompt && isIOS) {
      window.prompt(`ydppt://onEvent?event=${event}&params=${JSON.stringify(params)}`);
    } else {
      throw new Error('Android onEvent returned false');
    }
  } catch (error) {
    if (window.collectEvent) {
      window.collectEvent(event, params);
    }
  }
}

// 复制到剪贴板
export function contentCopy(content) {
  try {
    if (content.length == 0) {
      ElMessage.error({
        message: '内容为空不能复制哦',
        offset: 60
      });
      return
    }
    // 检查当前浏览器是否支持 Clipboard API
    if (!isSupported) {
      ElMessage.error({
        message: '您的浏览器不支持Clipboard API',
        offset: 60
      });
      return;
    }
    // 调用 copy 方法将目标文本复制到剪贴板
    copy(content);
    ElMessage.success({
      message: '内容已复制',
      offset: (window.innerHeight / 2) - 40
    });
  } catch (err) {
    ElMessage.error({
      message: '复制失败',
      offset: 60,
    });
  }
};

// 生成 uuid
export const generateUUID = () => {
  if (typeof crypto === 'object') {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
    if (typeof crypto.getRandomValues === 'function' && typeof Uint8Array === 'function') {
      const callback = (c) => {
        const num = Number(c)
        return (num ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (num / 4)))).toString(
          16
        )
      }
      return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, callback)
    }
  }
  let timestamp = new Date().getTime()
  let performanceNow =
    (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    let random = Math.random() * 16
    if (timestamp > 0) {
      random = (timestamp + random) % 16 | 0
      timestamp = Math.floor(timestamp / 16)
    } else {
      random = (performanceNow + random) % 16 | 0
      performanceNow = Math.floor(performanceNow / 16)
    }
    return (c === 'x' ? random : (random & 0x3) | 0x8).toString(16)
  })
}

/**
 * 判断用户是否是会员
 * @returns {Promise<boolean>} 返回一个 Promise，解析为布尔值表示是否为会员
 */
export const isVip = async (): Promise<boolean> => {
  try {
    // 检查是否有 token
    const token = getToken();
    if (!token) {
      // 没有 token，说明未登录，肯定不是会员
      return false;
    }

    const UserApi = await import('@/api/user.ts');

    // 调用用户信息接口
    const response = await UserApi.getUserInfo({});

    if (response.code === 'SUCCESS' && response.data) {
      // 通过 data.vip?.vip 字段判断是否为会员
      return response.data.vip?.vip || false;
    }

    return false;
  } catch (error) {
    console.error('检查会员状态时发生错误:', error);
    return false;
  }
};
